import { TsbGauge } from "@/components/charts/TsbGauge";
import { SkillRadar } from "@/components/charts/SkillRadar";
import { ScoreBars } from "@/components/charts/ScoreBars";
import { DOMAINS } from "@/lib/framework";
import { ShieldCheck, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle } from "lucide-react";

// A framed, screenshot-style mock report — no live data, illustration only.
function Frame({ label, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" data-testid="sample-report">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 truncate text-xs font-semibold text-slate-500">{label}</span>
        <span className="ml-auto rounded-full bg-[#78be21]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#5fa114]">
          Sample
        </span>
      </div>
      <div className="p-5 sm:p-6">{children}</div>
    </div>
  );
}

const STUDENT_SCORES = {
  intercultural_comm: 3.6, prof_ethics: 3.4, proj_task_mgmt: 3.0,
  critical_solving: 3.8, data_literacy: 3.2, systems_thinking: 2.8,
  digital_adapt: 2.6, collab_leadership: 3.5, creative_synthesis: 3.3, self_resilience: 3.0,
};

function StudentReport() {
  return (
    <Frame label="gpaplus.lonestar.edu / student / employment-profile">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#5fa114]">Diego Ramirez · Accounting</p>
          <h4 className="font-heading text-lg font-bold text-[#003057]">Transferable Skills Benchmark</h4>
        </div>
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">Advanced · 87 / 100</span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[auto,1fr] sm:items-center">
        <TsbGauge tsb={3.28} tsb100={76} size={180} />
        <SkillRadar
          height={230}
          series={[{ key: "you", name: "You", color: "#005291", scores: STUDENT_SCORES }]}
        />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-emerald-50 p-3">
          <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-700"><TrendingUp className="h-3.5 w-3.5" /> Top strength</p>
          <p className="mt-1 font-semibold text-[#003057]">Critical Thinking · 3.8</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3">
          <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700"><TrendingDown className="h-3.5 w-3.5" /> Grow next</p>
          <p className="mt-1 font-semibold text-[#003057]">Leadership · 2.6</p>
        </div>
      </div>
    </Frame>
  );
}

function EmployerReport() {
  const rows = [
    { name: "Grace Okafor", field: "Nursing", fit: 92, verified: true, skills: ["Communication", "Resilience", "Ethics"] },
    { name: "Tyler Brooks", field: "IT & Cyber", fit: 88, verified: true, skills: ["Problem Solving", "Critical Thinking"] },
    { name: "Diego Ramirez", field: "Accounting", fit: 84, verified: false, skills: ["Self-Management", "Ethics"] },
  ];
  return (
    <Frame label="gpaplus.lonestar.edu / employer / talent">
      <div className="flex items-center justify-between">
        <h4 className="font-heading text-lg font-bold text-[#003057]">Opt-in talent · skills-first</h4>
        <span className="text-xs font-semibold text-slate-500">Weighted for your role</span>
      </div>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.name} className="rounded-xl border border-slate-200 p-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#003057]">{r.name}</span>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#005291]/10 px-2 py-0.5 text-[10px] font-bold text-[#005291]">
                    <ShieldCheck className="h-3 w-3" /> 360 Verified
                  </span>
                )}
              </div>
              <span className="font-mono-num text-sm font-bold text-[#5fa114]">{r.fit}% fit</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">{r.field}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.skills.map((s) => (
                <span key={s} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">{s}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function EducatorReport() {
  const rows = [
    { name: "Diego Ramirez", tsb: "3.28", level: "Proficient", weak: "Leadership", action: "Team Lead micro-course", tone: "amber" },
    { name: "Grace Okafor", tsb: "3.61", level: "Advanced", weak: "Metacognition", action: "Reflective practice module", tone: "emerald" },
    { name: "Tyler Brooks", tsb: "2.94", level: "Developing", weak: "Communication", action: "Business writing workshop", tone: "blue" },
    { name: "Jordan (Undecided)", tsb: "2.71", level: "Developing", weak: "Self-Management", action: "Time-blocking clinic", tone: "blue" },
  ];
  const dot = { amber: "bg-amber-400", emerald: "bg-emerald-400", blue: "bg-blue-400" };
  return (
    <Frame label="gpaplus.lonestar.edu / advisor / caseload">
      <h4 className="font-heading text-lg font-bold text-[#003057]">Advisor caseload</h4>
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-bold">Student</th>
              <th className="px-3 py-2 font-bold">TSB</th>
              <th className="px-3 py-2 font-bold">Weakest</th>
              <th className="px-3 py-2 font-bold">Suggested next step</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.name}>
                <td className="px-3 py-2.5 font-semibold text-[#003057]">{r.name}</td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${dot[r.tone]}`} />
                    <span className="font-mono-num font-bold">{r.tsb}</span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{r.weak}</td>
                <td className="px-3 py-2.5 text-[#5fa114]">{r.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Frame>
  );
}

function AdminReport() {
  const domainBars = DOMAINS.map((d) => ({
    name: d.short,
    value: d.id === "execution" ? 3.21 : d.id === "cognitive" ? 3.05 : 2.86,
    color: d.color,
  }));
  return (
    <Frame label="gpaplus.lonestar.edu / admin / analytics">
      <h4 className="font-heading text-lg font-bold text-[#003057]">Cohort analytics · Fall 2026</h4>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {[
          { k: "Students", v: "4,812" },
          { k: "Avg TSB", v: "3.04" },
          { k: "Advanced", v: "31%" },
        ].map((s) => (
          <div key={s.k} className="rounded-xl bg-slate-50 p-3 text-center">
            <p className="font-mono-num text-xl font-bold text-[#003057]">{s.v}</p>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{s.k}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Domain averages</p>
      <ScoreBars items={domainBars} height={150} layout="horizontal" />
      <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-amber-900"><span className="font-bold">Equity flag:</span> First-gen cohort trails 0.28 TSB in Growth & Leadership — targeted support recommended.</p>
      </div>
      <div className="mt-2 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
        <p className="text-emerald-900"><span className="font-bold">Audit trail:</span> 128 score-affecting changes logged this term — all traceable.</p>
      </div>
    </Frame>
  );
}

const VARIANTS = {
  student: StudentReport,
  employer: EmployerReport,
  educator: EducatorReport,
  administrator: AdminReport,
};

export function SampleReport({ variant }) {
  const Cmp = VARIANTS[variant] || StudentReport;
  return <Cmp />;
}
